import { useMemo, forwardRef, ForwardedRef } from "react";
import { Button as ChakraButton, ButtonProps, Center, IconButtonProps, useToken } from "@chakra-ui/react";
import { Link as ReactRouterLink, LinkProps as RouterLinkProps } from "react-router-dom";

export const Button = forwardRef(
  (
    { colorScheme = "blue", variant = "solid", disabled, ...props }: ButtonProps,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const isAlpha = colorScheme.includes("alpha");
    const isDark = isAlpha && colorScheme.includes("Dark");

    const [color, background] = useToken("colors", [isDark ? "white" : "info", `${colorScheme}.500`]);

    const variantProps = useMemo(() => {
      if (disabled && !isAlpha) return {};

      if (disabled && isAlpha) {
        return {
          css: {
            ":disabled, :hover[disabled]": {
              color,
              background,
              opacity: 0.3,
              cursor: "not-allowed",
            },
          },
        };
      }

      if (isAlpha && variant === "solid") return { color };

      switch (variant) {
        case "outline":
          return {
            color: `${colorScheme}.500`,
            _hover: { color: "white", borderColor: `${colorScheme}.500`, bg: `${colorScheme}.500` },
            _active: { color: "white", borderColor: `${colorScheme}.600`, bg: `${colorScheme}.600` },
          } as ButtonProps;

        case "ghost":
          return {
            color: `${colorScheme}.500`,
            _hover: { color: `${colorScheme}.600` },
            _active: { color: `${colorScheme}.700` },
            boxShadow: "none",
          } as ButtonProps;

        default:
          return {};
      }
    }, [colorScheme, variant, disabled, isAlpha, color, background]);

    return (
      <ChakraButton
        ref={ref}
        colorScheme={colorScheme}
        variant={variant}
        disabled={disabled}
        {...variantProps}
        {...props}
      />
    );
  }
);

export const ButtonLink = forwardRef(
  ({ to, children, disabled, ...props }: ButtonProps & RouterLinkProps, ref: ForwardedRef<HTMLButtonElement>) => (
    <Button ref={ref} p="0" disabled={disabled} {...props}>
      {disabled ? (
        children
      ) : (
        <Center as={ReactRouterLink} to={to} h="full" w="full">
          {children}
        </Center>
      )}
    </Button>
  )
);

export const IconButton = forwardRef(
  ({ icon, children, ...props }: IconButtonProps, ref: ForwardedRef<HTMLButtonElement>) => (
    <Button ref={ref} p="0" {...props}>
      {icon || children}
    </Button>
  )
);
